/**
 * BudgetsPanel — G3 : l'onglet Budgets de la Comptabilité (vague 4d, 3.14).
 *
 * La Comptabilité POSSÈDE : elle crée les enveloppes annuelles par secteur
 * (noms libres — Voirie, Bâtiments, Fêtes… comme son budget réel), voit le
 * consommé calculé depuis le LIVRE des mouvements (chaque débit remonte au
 * devis accepté, chaque re-crédit à son annulation), et le global en un
 * coup d'œil. Le dépassement s'affiche — il n'a jamais bloqué personne.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiException, type BudgetSuivi, type BudgetMouvement } from '@/lib/api';
import { Card, Button, Badge, Alert } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { Wallet, Plus, AlertCircle, Check, Trash2 } from 'lucide-react';

const CHAMP = 'h-9 rounded-r-md border border-border bg-surface/60 px-2.5 text-sm text-ink focus:border-accent focus:outline-none';

function euros(n: number): string {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);
}

function dateFr(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', dateStyle: 'short' })
    .format(new Date(iso));
}

export function BudgetsPanel({ estGestion }: { estGestion: boolean }) {
  const anneeCourante = new Date().getFullYear();
  const [annee, setAnnee] = useState(anneeCourante);
  const [data, setData] = useState<BudgetSuivi | null>(null);
  const [mouvements, setMouvements] = useState<BudgetMouvement[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [fSecteur, setFSecteur] = useState('');
  const [fMontant, setFMontant] = useState('');
  const { confirm, dialogElement } = useConfirmDialog();

  const charger = useCallback(async () => {
    setError(null);
    try {
      const [b, m] = await Promise.all([
        api.budgets.lister(annee),
        api.budgets.mouvements(annee),
      ]);
      setData(b);
      setMouvements(m.mouvements);
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Chargement impossible');
    }
  }, [annee]);

  useEffect(() => { void charger(); }, [charger]);

  async function enregistrer() {
    setError(null); setMsg(null);
    try {
      await api.budgets.upsert({
        annee, secteur: fSecteur.trim(),
        montantAlloue: Number(fMontant.replace(',', '.')),
      });
      setFSecteur(''); setFMontant('');
      setMsg('Enveloppe enregistrée.');
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Enregistrement impossible');
    }
  }

  return (
    <div className="space-y-3">
      {dialogElement}
      {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
      {msg && <Alert variant="success" icon={Check}>{msg}</Alert>}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-medium text-ink">
          <Wallet className="h-4 w-4" aria-hidden="true" /> Budgets par secteur
        </h3>
        <select value={annee} onChange={(e) => setAnnee(Number(e.target.value))}
          aria-label="Année" className={CHAMP}>
          {[anneeCourante + 1, anneeCourante, anneeCourante - 1].map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
      </div>

      {data && (
        <Card variant="glass" padded>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="text-ink-muted">Global {data.annee}</span>
            <span className="text-ink">
              {euros(data.global.consomme)} consommés / {euros(data.global.alloue)}
              {' — '}
              <span className={data.global.restant < 0 ? 'font-medium text-danger' : 'font-medium'}>
                {euros(data.global.restant)} restants
              </span>
            </span>
          </div>
          <div className="space-y-1.5">
            {data.secteurs.map((s) => {
              const pct = s.montantAlloue > 0
                ? Math.min(100, Math.round((s.consomme / s.montantAlloue) * 100)) : 100;
              return (
                <div key={s.id} className="rounded-r-lg border border-border/60 px-2.5 py-1.5">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <span className="font-medium text-ink">{s.secteur}</span>
                    <span className="flex items-center gap-1.5 text-xs text-ink-muted">
                      {euros(s.consomme)} / {euros(s.montantAlloue)}
                      {s.enDepassement && <Badge variant="urgent" size="sm">Dépassé</Badge>}
                      {estGestion && (
                        <button type="button" aria-label={`Supprimer l'enveloppe ${s.secteur}`}
                          onClick={() => {
                            void (async () => {
                              const ok = await confirm({
                                title: `Supprimer l'enveloppe « ${s.secteur} » ?`,
                                description: 'Les mouvements du livre sont conservés.',
                                confirmLabel: 'Supprimer',
                              });
                              if (ok) {
                                try { await api.budgets.supprimer(s.id); await charger(); }
                                catch { setError('Suppression impossible'); }
                              }
                            })();
                          }}
                          className="text-ink-muted transition-colors hover:text-danger">
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      )}
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface">
                    <div className={`h-full rounded-full ${s.enDepassement ? 'bg-danger' : 'bg-accent'}`}
                      style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            {data.secteurs.length === 0 && (
              <p className="text-sm text-ink-muted">Aucune enveloppe pour {data.annee}.</p>
            )}
          </div>

          {estGestion && (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border/60 pt-3">
              <input placeholder="Secteur (ex : Voirie)" value={fSecteur}
                onChange={(e) => setFSecteur(e.target.value)}
                aria-label="Secteur" className={`${CHAMP} min-w-40`} />
              <input inputMode="decimal" placeholder="Montant alloué €" value={fMontant}
                onChange={(e) => setFMontant(e.target.value)}
                aria-label="Montant alloué" className={CHAMP} />
              <Button variant="primary" size="sm"
                disabled={!fSecteur.trim() || !fMontant || Number.isNaN(Number(fMontant.replace(',', '.')))}
                onClick={() => void enregistrer()}>
                <Plus className="h-4 w-4" aria-hidden="true" /> Enregistrer
              </Button>
            </div>
          )}
        </Card>
      )}

      {mouvements.length > 0 && (
        <Card variant="glass" padded>
          <h4 className="mb-2 text-sm font-medium text-ink">Livre des mouvements</h4>
          <div className="space-y-1">
            {mouvements.map((m) => (
              <p key={m.id} className="text-xs text-ink-muted">
                <span className="text-ink">{dateFr(m.createdAt)}</span>
                {' · '}{m.secteur}{' · '}
                <span className={m.sens === 'debit' ? 'text-danger' : 'text-success'}>
                  {m.sens === 'debit' ? '−' : '+'}{euros(m.montant)}
                </span>
                {' · '}{m.motif}
              </p>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
